import { useEffect, useState } from "react";
import { useParams, Navigate } from "react-router-dom";
import { BACKEND_URL } from "@/lib/auth";
import NotFoundPage from "@/pages/NotFound";

/** Catch-all-Route für Vertreter-Kurzlinks à la `hypefly.de/7`.
 *  - Prüft, ob das Segment eine reine Ziffernfolge ist (sonst 404).
 *  - Löst die Nummer im Backend zum internen `code` auf.
 *  - Schreibt `hf_vt_code` in `sessionStorage` (analog zur bestehenden
 *    `?vt=`-Logik in `analytics.ts`).
 *  - Redirect auf `/`, damit die Adresszeile sauber bleibt und der
 *    `useAnalytics()`-Hook den `page_view` mit dem VT-Code aufzeichnet.
 */
export default function VtCatch() {
    const { vtnum = "" } = useParams();
    const [state, setState] = useState<"checking" | "ok" | "notfound">("checking");

    useEffect(() => {
        // Reine Ziffernfolge? Sonst direkt in den 404-State.
        if (!/^\d+$/.test(vtnum)) {
            setState("notfound");
            return;
        }
        let alive = true;
        void (async () => {
            try {
                const res = await fetch(`${BACKEND_URL}/api/vertreter/lookup/${vtnum}`, {
                    method: "GET",
                    credentials: "omit",
                });
                if (!alive) return;
                if (!res.ok) {
                    setState("notfound");
                    return;
                }
                const data = (await res.json()) as { code?: string };
                const code = (data.code || "").trim().toLowerCase();
                if (!code) {
                    setState("notfound");
                    return;
                }
                try {
                    sessionStorage.setItem("hf_vt_code", code);
                } catch {
                    /* privater Modus / disabled Storage → weiterschicken */
                }
                setState("ok");
            } catch {
                setState("notfound");
            }
        })();
        return () => {
            alive = false;
        };
    }, [vtnum]);

    if (state === "notfound") return <NotFoundPage />;
    if (state === "ok") return <Navigate to="/" replace />;
    // Kurzer Loading-State während der Lookup läuft. Kein sichtbarer Flash,
    // weil das i. d. R. < 200 ms dauert.
    return (
        <div
            className="min-h-screen flex items-center justify-center"
            data-testid="vt-catch-loading"
        >
            <div className="text-sm text-hf-muted">Einen Moment…</div>
        </div>
    );
}
